(ns app.front.view.group.admin.fees.template.template-events
  (:require [app.domain.fee.model :as fee-model]
            [app.domain.fee.validate :as validate]
            [app.front.entities :as entities]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(rf/reg-event-db ::select-fee-template
  (fn [db [_ fee-template-id]]
    (assoc-in db [::fee-templates :selected-fee-template] fee-template-id)))


(rf/reg-event-db ::change-field
  (fn [db [_ fee-template-id field value]]
    ;; fee-template-id is either the DB ID of an existing FeeTemplate or :new when creating a new one
    (assoc-in db [::fee-templates :unsaved-changes fee-template-id field] value)))


(rf/reg-event-db ::allow-penalty-check
  (fn [db [_ fee-template-id value]]
    (assoc-in db [::fee-templates :unsaved-changes fee-template-id :fee-template/allow-penalty?] value)))


(defn remove-if-nil [payload attr]
  (cond-> payload
    (-> payload (get attr) nil?) (dissoc attr)))


(rf/reg-event-fx ::change-formula
  (fn [{:keys [db]} [_ fee-template-id field value]]
    {:db                (assoc-in db [::fee-templates :unsaved-changes fee-template-id field] value)
     :dispatch          [::validate-cycle fee-template-id field value]
     :dispatch-debounce {:id      ::formula-validation
                         :timeout 1000
                         :action  :dispatch
                         :event   [::validate-formula field value]}}))


(rf/reg-event-fx ::validate-cycle
  (fn [{:keys [db]} [_ fee-template-id field value]]
    (let [fee-schedule  (get-in db [::fee-templates :fee-schedule])
          fee-templates (fee-model/schedule-fee-templates fee-schedule)
          fee-variables (fee-model/schedule-fee-variables fee-schedule)
          cycle-result  (validate/fee-cycle? fee-templates fee-variables fee-template-id {field value})
          request-id    (str "fee-dependency-cycle-" fee-template-id)]
      (if cycle-result
        (let [error (validate/humanize-cycle-error cycle-result fee-templates)]
          {:db       (assoc-in db [::fee-templates :errors fee-template-id :cycle] error)
           :dispatch [:notifications/upsert
                      :toast
                      :error
                      :error/error
                      (map vector error)
                      request-id]})
        {:db       (update-in db [::fee-templates :errors fee-template-id] dissoc :cycle)
         :dispatch [:notifications/remove request-id]}))))

(defn filter-penalty-fields
  "Remove penalty-related fields if allow-penalty is false."
  [payload]
  (if-not (:fee-template/allow-penalty? payload)
    (dissoc payload
            :fee-template/apply-grace-period?
            :fee-template/penalty-rate
            :fee-template/penalty-rate-type
            :fee-template/interest-rate
            :fee-template/interest-rate-type
            :fee-template/interest-recurrence
            :fee-template/interest-occurrence-limit)
    payload))

(defn- penalty-changes
  "Penalties are allowed by the phase the template ends up with and its flag; a phase that excludes them clears the
   flag, and the rate fields go along with it."
  [payload existing-fee]
  (let [phase          (:bill/phase payload (:bill/phase existing-fee))
        allow-penalty? (:fee-template/allow-penalty? payload (:fee-template/allow-penalty? existing-fee))]
    (filter-penalty-fields
      (if (fee-model/penalty-allowed-for-phase? phase)
        (p/assoc-if payload :fee-template/allow-penalty? allow-penalty?)
        (assoc payload :fee-template/allow-penalty? false)))))

(rf/reg-event-fx ::save
  (fn [{:keys [db]} [_ fee-template-id]]
    (let [id                 (get-in db [::fee-templates :selected-fee-template])
          fee-schedule-id    (-> db ::fee-templates :fee-schedule :db/id)
          authority-group-id (-> db :app.front.view.group.admin.fees.schedule.schedule-events/fees :authority-group-id)
          creating-new?      (= id :new)
          payload            (get-in db [::fee-templates :unsaved-changes fee-template-id])]
      (if creating-new?
        {:comm/command {:command    :fee/create-fee-template
                        :method     :post
                        :data       {:fee-schedule-id    fee-schedule-id
                                     :authority-group-id authority-group-id
                                     :data               (-> payload
                                                             (remove-if-nil :fee-template/group)
                                                             (penalty-changes nil))}
                        :on-success [::create-success]}}
        (let [existing-fee (get-in db [::entities/entity-db id])]
          {:comm/command {:command    :fee/edit-fee-template
                          :method     :post
                          :data       {:fee-template-id    fee-template-id
                                       :authority-group-id authority-group-id
                                       :changes            (penalty-changes payload existing-fee)}
                          :on-success [::save-success]}})))))


(rf/reg-event-db
  ::cancel
  (fn [db _]
    (-> db
        (p/dissoc-in [::fee-templates :selected-fee-template])
        (p/dissoc-in [::fee-templates :unsaved-changes]))))


(rf/reg-event-fx
  ::validate-formula
  (fn [{:keys [db]} [_ type formula]]
    {:db           (assoc-in db [::fee-templates :formulas type :status] :loading)
     :comm/command {:command    :fee/validate-formula
                    :method     :post
                    :data       {:formula formula}
                    :on-success [::formula-validated type]}}))


(rf/reg-event-db
  ::formula-validated
  (fn [db [_ formula _ response]]
    (let [status (if (:valid? response)
                   :valid
                   :invalid)]
      (-> db
          (assoc-in [::fee-templates :formulas formula :status] status)
          (assoc-in [::fee-templates :formulas formula :message] (:message response))))))


(rf/reg-event-db
  ::unselect-group ;; Called when the group is deleted so it doesn't haunt the unsaved changes
  (fn [db [_ fee-group-id]]
    (let [dissoc-template-ids  (->> db
                                    ::fee-templates
                                    :unsaved-changes
                                    (filter (fn [[_ {:fee-template/keys [group]}]]
                                              (= group fee-group-id)))
                                    keys)
          dissoc-unsaved-group (fn [db template-id]
                                 (p/dissoc-in db [::fee-templates :unsaved-changes template-id :fee-template/group]))]
      (reduce dissoc-unsaved-group db dissoc-template-ids))))


(rf/reg-event-fx
  ::delete
  (fn [_ [_ fee-template-id]]
    {:comm/command {:command    :fee/delete-fee-template
                    :method     :post
                    :data       {:fee-template-id fee-template-id}
                    :on-success [::delete-success]}}))


(rf/reg-event-fx
  ::create-success
  (fn [{:keys [db]} [_ status changes]]
    (let [created-template-id (some (fn [[entity-id {type :entity/type}]]
                                      (when (= type [:assoc :entity.type/fee-template])
                                        entity-id))
                                    changes)]
      {:db       (-> db
                     (p/dissoc-in [::fee-templates :unsaved-changes])
                     (assoc-in [::fee-templates :selected-fee-template] created-template-id))
       :dispatch [::entities/apply-changes status changes]})))


(rf/reg-event-fx
  ::save-success
  (fn [{:keys [db]} [_ status changes]]
    {:db         (p/dissoc-in db [::fee-templates :unsaved-changes])
     :dispatch-n [[::entities/apply-changes status changes]
                  [:notifications/add :toast :success :success :changes-saved]]}))


(rf/reg-event-fx
  ::delete-success
  (fn [{:keys [db]} [_ status changes]]
    {:dispatch [::entities/apply-changes status changes]
     :db       (-> db
                   (p/dissoc-in [::fee-templates :selected-fee-template])
                   (p/dissoc-in [::fee-templates :unsaved-changes]))}))


(rf/reg-event-db
  ::create-new-fee-template
  (fn [db _]
    (assoc-in db [::fee-templates :selected-fee-template] :new)))


(rf/reg-event-db
  ::toggle-unit-use
  (fn [db [_ fee-template-id unit-enabled?]]
    (assoc-in db [::fee-templates :unsaved-changes fee-template-id :fee-template/unit-enabled?] unit-enabled?)))
